import React, { useEffect, useRef, useState } from 'react';
import styles from '../Attachment.module.scss';
import { AttachmentNameDisplay } from './GenerticAttachment';
import { IFile } from '../../../../types';
import { AnimatedDiv } from '../../../../animation';
import AriaButton from '../../../../components/AriaComponents/AriaButton';
import { useTypedDispatch } from '../../../../redux/hooks';
import { nebulaChatApi } from '../../../../redux/services/nebulaChat';
import { setFilePreview } from '../../../../redux/slices';
import Icon from '../../../../components/Icon';
import LoadingSpinner from '../../../../components/loading/LoadingSpinner';

interface ImageAttachmentProps {
    file: IFile;
    mini: boolean;
    showOptions: boolean;
    onDownload: () => void;
}

export const DownloadButton: React.FC<{ download: () => void }> = ({ download }) => (
    <AriaButton onClick={download} className={styles.download_button}>
        <Icon name='arrowDownWithLine' />
    </AriaButton>
);

export const ImageAttachment: React.FC<ImageAttachmentProps> = ({
    file,
    mini,
    showOptions,
    onDownload,
}) => {
    const dispatch = useTypedDispatch();

    const containerRef = useRef<HTMLDivElement>(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        if (!containerRef.current) return undefined;
        const container = containerRef.current;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setIsInView(true);
                observer.disconnect();
            }
        }, { threshold: 0.1 });
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    const isGif = file.content_type === 'image/gif';

    const { data: thumbnailUrl, isLoading } = nebulaChatApi.endpoints.pullFileN.useQuery({
        file_uuid: file.uuid,
        thumbnail: true,
    }, { skip: !isInView || file.pending });

    const [previewRequested, setPreviewRequested] = useState(false);
    const dispatchOnLoad = useRef(false);

    const { data: fullResUrl } = nebulaChatApi.endpoints.pullFileN.useQuery({
        file_uuid: file.uuid,
        thumbnail: false,
    }, { skip: isGif ? !thumbnailUrl : !previewRequested });

    useEffect(() => {
        if (fullResUrl && dispatchOnLoad.current) {
            dispatchOnLoad.current = false;
            dispatch(setFilePreview({
                src: fullResUrl,
                file_name: file.name,
                file_type: 'image',
                file_size: file.size,
                shared_by: file.shared_by,
                shared_at: file.shared_at as any,
                uuid: file.uuid,
            }));
        }
    }, [fullResUrl]);

    const activatePreview = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (fullResUrl) {
            dispatch(setFilePreview({
                src: fullResUrl,
                file_name: file.name,
                file_type: 'image',
                file_size: file.size,
                shared_by: file.shared_by,
                shared_at: file.shared_at as any,
                uuid: file.uuid,
            }));
        } else {
            dispatchOnLoad.current = true;
            setPreviewRequested(true);
        }
    };

    const showSpinner = !isInView || isLoading || !thumbnailUrl || file.pending;

    return (
        <>
            <div
                className={mini ? styles.preview_container_mini : styles.preview_container}
                style={(!mini && showSpinner) ? { display: 'grid', placeContent: 'center' } : undefined}
                ref={containerRef}
            >
                {showSpinner ? (
                    <LoadingSpinner color='mamba' />
                ) : (
                    <img
                        className='image-preview'
                        src={fullResUrl || thumbnailUrl}
                        alt={file.name}
                        onClick={activatePreview}
                    />
                )}
            </div>
            <AnimatedDiv
                visible={mini || showOptions}
                className={styles.file_content_container}
                onClick={e => !mini && activatePreview(e)}
                inAnimation={['fade', 'slideFromBottom']}
                transition={{ duration: 0.4 }}
            >
                <AttachmentNameDisplay file={file} />
                {!mini && <DownloadButton download={onDownload} />}
            </AnimatedDiv>
        </>
    );
};
